import { useEffect, useMemo, useState } from "react";

import { LoadingDiv, LoadingDivShort, SweetAlertShow } from "../master/Master";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";

import {
  studentAttendanceDashboardV4,
  studentAttendanceDashboard2V4,
  studentAttendanceRangeSummary,
} from "../../api/Attendance";

import DashboardModal from "./components/DashboardModal";
import { printElement, printElementV2, printImg } from "../master/Print";
import { printAttendanceDashboard } from "../master/PrintReport";

function QuickReportCard({
  title,
  subtitle,
  icon,
  tone, // "primary" | "danger" | "success" | "warning" | "info"
  count, // optional
  loading,
  onClick,
}) {
  return (
    <div
      className="card border-0 shadow-sm h-100 quick-report-card"
      style={{ cursor: "pointer" }}
      onClick={onClick}
      data-bs-toggle="modal"
      data-bs-target="#QuickReportModal"
    >
      <div className="card-body d-flex align-items-center gap-3 p-3">
        <div
          className={`rounded-3 bg-${tone}-subtle text-${tone} d-flex align-items-center justify-content-center flex-shrink-0`}
          style={{ width: 48, height: 48 }}
        >
          <i className={`${icon} fs-4`}></i>
        </div>

        {/* min-width: 0 lets this block shrink instead of pushing the badge out */}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <h6 className="fw-bold mb-0 text-truncate" title={title}>
            {title}
          </h6>
          <small className="text-muted d-block text-truncate" title={subtitle}>
            {subtitle}
          </small>
        </div>

        {count !== undefined && (
          <span
            className={`badge bg-${tone}-subtle text-${tone} flex-shrink-0`}
          >
            {loading ? <LoadingDivShort /> : count}
          </span>
        )}

        <i className="ri-arrow-right-s-line flex-shrink-0"></i>
      </div>
    </div>
  );
}

export default function StudentAttendanceDashboard() {
  /* =========================================================
     STATE
  ========================================================= */

  const [loading, setLoading] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [sessions, setSessions] = useState([]);

  const [data, setData] = useState({});
  const [analyticsData, setAnalyticsData] = useState({});
  const [dataSummary, setDataSummary] = useState({});

  const [allFilters, setAllFilters] = useState({});

  /*
   * Quick report modal.
   *
   * message
   * lowAttendance
   * attendanceSummary
   * longAbsent
   * staffWise
   */
  const [quickReport, setQuickReport] = useState(null);

  /* =========================================================
     DATE HELPERS
  ========================================================= */

  const today = new Date();

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  /* =========================================================
     FILTERS
  ========================================================= */

  const [filters, setFilters] = useState({
    date: formatDate(today),

    month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      "0",
    )}`,

    from_date: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),

    to_date: formatDate(today),
  });

  /* =========================================================
     LOAD SESSIONS
  ========================================================= */

  useEffect(() => {
    try {
      const storedSessions = JSON.parse(
        localStorage.getItem("sessions") ?? "[]",
      );

      setSessions(storedSessions);
    } catch (error) {
      console.error("Session parse error:", error);
      setSessions([]);
    }
  }, []);

  /* =========================================================
     SET ACTIVE SESSION
  ========================================================= */

  useEffect(() => {
    const activeSession = sessions?.find(
      (session) => Number(session.status) === 1,
    )?.id;

    if (!activeSession) {
      return;
    }

    setFilters((prev) => {
      if (prev.session_id) {
        return prev;
      }

      return {
        ...prev,
        session_id: activeSession,
      };
    });
  }, [sessions]);

  /* =========================================================
     BUILD QUERY
  ========================================================= */

  const buildQuery = (customFilters = {}) => {
    const finalFilters = {
      ...filters,
      ...customFilters,
    };

    return Object.keys(finalFilters)
      .filter(
        (key) =>
          finalFilters[key] !== undefined &&
          finalFilters[key] !== null &&
          finalFilters[key] !== "",
      )
      .map((key) => `${key}=${encodeURIComponent(finalFilters[key])}`)
      .join("&");
  };

  /* =========================================================
     ATTENDANCE REPORT
  ========================================================= */

  const getAttendance = async () => {
    setLoading(true);

    try {
      const query = buildQuery();

      const response = await studentAttendanceDashboardV4(`&${query}`);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load attendance",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.response?.message ||
          "Unable to load attendance report",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     BATCH / ANALYTICS REPORT
  ========================================================= */

  const getAttendanceAnalytics = async () => {
    setLoadingAnalytics(true);

    try {
      const query = buildQuery();

      const response = await studentAttendanceDashboard2V4(`&${query}`);

      if (response?.data?.status) {
        setAnalyticsData(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load batch report",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.response?.message ||
          "Unable to load batch report",
      );
    } finally {
      setLoadingAnalytics(false);
    }
  };

  /* =========================================================
     ATTENDANCE RANGE SUMMARY
  ========================================================= */

  const getRangeSummary = async () => {
    setLoadingSummary(true);

    try {
      const query = buildQuery();

      const response = await studentAttendanceRangeSummary(`&${query}`);

      if (response?.data?.status) {
        setDataSummary(response?.data?.data || {});
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load attendance summary",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.response?.message ||
          "Unable to load attendance summary",
      );
    } finally {
      setLoadingSummary(false);
    }
  };

  /* =========================================================
     LOAD DAILY REPORT
  ========================================================= */

  useEffect(() => {
    if (!filters.session_id) {
      return;
    }

    getAttendance();
    getAttendanceAnalytics();
  }, [filters.session_id, filters.date]);

  /* =========================================================
     LOAD RANGE SUMMARY
  ========================================================= */

  useEffect(() => {
    if (!filters.session_id) {
      return;
    }

    getRangeSummary();
  }, [filters.session_id, filters.from_date, filters.to_date]);

  /* =========================================================
     PERCENTAGES
  ========================================================= */

  const attendancePercentage = useMemo(() => {
    const total = Number(data?.total || 0);

    if (!total) {
      return {
        present: 0,
        absent: 0,
        notMarked: 0,
      };
    }

    return {
      present: Math.round((Number(data?.present || 0) / total) * 100),

      absent: Math.round((Number(data?.absent || 0) / total) * 100),

      notMarked: Math.round((Number(data?.not_marked || 0) / total) * 100),
    };
  }, [data]);

  /* =========================================================
     OPEN DETAIL REPORT
  ========================================================= */

  const openStudentReport = (extraFilters = {}) => {
    setAllFilters({
      ...filters,
      table: "studentAttendance",
      ...extraFilters,
    });
  };

  /* =========================================================
     OPEN QUICK REPORT
  ========================================================= */

  const openQuickReport = (type) => {
    setQuickReport(type);
  };

  /* =========================================================
     REFRESH
  ========================================================= */

  const refreshDashboard = () => {
    getAttendance();
    getAttendanceAnalytics();
    getRangeSummary();
  };

  /* =========================================================
     STAFF WISE ATTENDANCE
  ========================================================= */

  const staffWiseData = analyticsData?.staff_wise || [];

  /* =========================================================
     MAIN RENDER
  ========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard">
        <div className="layout-container">
          {/* SIDEBAR */}
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">
            {/* NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>

            <div className="content-wrapper">
              <div
                className="container-fluid flex-grow-1 container-p-y"
                id="PrintDiv"
              >
                {/* =================================================
                                    HEADER
                                ================================================= */}

                <div className="row mb-3 no-print">
                  <Master />

                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="row align-items-center g-3">
                          {/* TITLE */}

                          <div className="col-lg-5">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                                style={{
                                  width: "52px",
                                  height: "52px",
                                }}
                              >
                                <i className="ri-calendar-check-line fs-3"></i>
                              </div>

                              <div>
                                <h4 className="fw-bold mb-1">
                                  Attendance Report
                                </h4>

                                <p className="text-muted mb-0">
                                  Daily student attendance overview
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* FILTERS */}

                          <div className="col-lg-7">
                            <div className="d-flex justify-content-lg-end flex-wrap gap-2">
                              <div
                                className="form-floating form-floating-outline"
                                style={{
                                  minWidth: "190px",
                                }}
                              >
                                <select
                                  className="form-select"
                                  value={filters?.session_id || ""}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      session_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">Select Session</option>

                                  {sessions?.map((session) => (
                                    <option key={session.id} value={session.id}>
                                      {session.session}

                                      {Number(session.status) === 1
                                        ? " : Active"
                                        : " : Inactive"}
                                    </option>
                                  ))}
                                </select>

                                <label>Session</label>
                              </div>

                              <div
                                className="form-floating form-floating-outline"
                                style={{
                                  minWidth: "170px",
                                }}
                              >
                                <input
                                  type="date"
                                  className="form-control"
                                  value={filters?.date || ""}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      date: e.target.value,
                                    })
                                  }
                                />

                                <label>Attendance Date</label>
                              </div>

                              <button
                                type="button"
                                className="btn btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() =>
                                  printAttendanceDashboard({
                                    data,
                                    analyticsData,
                                    dataSummary,
                                    percentage: attendancePercentage,
                                    sessionName:
                                      sessions?.find(
                                        (s) =>
                                          String(s.id) ===
                                          String(filters?.session_id),
                                      )?.session || "-",
                                    date: filters?.date,
                                    fromDate: filters?.from_date,
                                    toDate: filters?.to_date,
                                  })
                                }
                              >
                                <i className="ri-printer-line"></i>
                                Print
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                                    MAIN ATTENDANCE CARDS
                                ================================================= */}

                <div className="row g-3 mb-3">
                  {/* TOTAL */}

                  <div className="col-xl-3 col-md-6">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="text-muted mb-1">Total Students</p>

                            <h3 className="fw-bold mb-0">
                              {loading ? <LoadingDivShort /> : data?.total || 0}
                            </h3>

                            <small className="text-muted">
                              Active students
                            </small>
                          </div>

                          <div className="rounded-3 bg-primary-subtle text-primary p-3">
                            <i className="ri-group-line fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* PRESENT */}

                  <div className="col-xl-3 col-md-6">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        cursor: "pointer",
                      }}
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          attendance: "1",
                        })
                      }
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="text-muted mb-1">Present</p>

                            <h3 className="fw-bold text-success mb-0">
                              {loading ? (
                                <LoadingDivShort />
                              ) : (
                                data?.present || 0
                              )}
                            </h3>

                            <small className="text-success">
                              {attendancePercentage.present}% attendance
                            </small>
                          </div>

                          <div className="rounded-3 bg-success-subtle text-success p-3">
                            <i className="ri-checkbox-circle-line fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ABSENT */}

                  <div className="col-xl-3 col-md-6">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        cursor: "pointer",
                      }}
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          attendance: "2",
                        })
                      }
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="text-muted mb-1">Absent</p>

                            <h3 className="fw-bold text-danger mb-0">
                              {loading ? (
                                <LoadingDivShort />
                              ) : (
                                data?.absent || 0
                              )}
                            </h3>

                            <small className="text-danger">
                              {attendancePercentage.absent}% absent
                            </small>
                          </div>

                          <div className="rounded-3 bg-danger-subtle text-danger p-3">
                            <i className="ri-close-circle-line fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* NOT MARKED */}

                  <div className="col-xl-3 col-md-6">
                    <div
                      className="card border-0 shadow-sm h-100"
                      style={{
                        cursor: "pointer",
                      }}
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          attendance: "0",
                        })
                      }
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="text-muted mb-1">Not Marked</p>

                            <h3 className="fw-bold text-warning mb-0">
                              {loading ? (
                                <LoadingDivShort />
                              ) : (
                                data?.not_marked || 0
                              )}
                            </h3>

                            <small className="text-warning">
                              {attendancePercentage.notMarked}% pending
                            </small>
                          </div>

                          <div className="rounded-3 bg-warning-subtle text-warning p-3">
                            <i className="ri-time-line fs-4"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QUICK REPORTS */}
                <div className="row g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-xl-3 row-cols-xxl-5">
                  <div className="col">
                    <QuickReportCard
                      title="Message Report"
                      subtitle="Push & Meta WhatsApp"
                      icon="ri-message-3-line"
                      tone="primary"
                      onClick={() => openQuickReport("message")}
                    />
                  </div>

                  <div className="col">
                    <QuickReportCard
                      title="Low Attendance"
                      subtitle="Below 75%"
                      icon="ri-arrow-down-line"
                      tone="danger"
                      count={analyticsData?.low_attendance_below_75 || 0}
                      loading={loadingAnalytics}
                      onClick={() => openQuickReport("lowAttendance")}
                    />
                  </div>

                  <div className="col">
                    <QuickReportCard
                      title="Attendance Summary"
                      subtitle="Date range"
                      icon="ri-file-list-3-line"
                      tone="success"
                      onClick={() => openQuickReport("attendanceSummary")}
                    />
                  </div>

                  <div className="col">
                    <QuickReportCard
                      title="Long Absent"
                      subtitle="Continuous absence"
                      icon="ri-user-unfollow-line"
                      tone="warning"
                      count={analyticsData?.long_absent || 0}
                      loading={loadingAnalytics}
                      onClick={() => openQuickReport("longAbsent")}
                    />
                  </div>

                  <div className="col">
                    <QuickReportCard
                      title="Staff Wise Attendance"
                      subtitle="By class teacher"
                      icon="ri-user-settings-line"
                      tone="info"
                      count={staffWiseData?.length || 0}
                      loading={loadingAnalytics}
                      onClick={() => openQuickReport("staffWise")}
                    />
                  </div>
                </div>

                {/* =================================================
                                    BATCH WISE REPORT
                                ================================================= */}

                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-transparent border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="fw-bold mb-1">Batch Wise Attendance</h5>

                        <small className="text-muted">
                          Click any batch to view student details
                        </small>
                      </div>

                      <span className="badge bg-primary-subtle text-primary">
                        {analyticsData?.batch_wise?.length || 0} Batches
                      </span>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">#</th>

                          <th>Batch</th>

                          <th className="text-center">Total</th>

                          <th className="text-center text-success">Present</th>

                          <th className="text-center text-danger">Absent</th>

                          <th className="text-center text-warning">
                            Not Marked
                          </th>

                          <th className="text-center">Attendance</th>

                          <th className="text-center no-print">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loadingAnalytics ? (
                          <tr>
                            <td colSpan={8} className="text-center py-5">
                              <LoadingDivShort />
                            </td>
                          </tr>
                        ) : analyticsData?.batch_wise?.length ? (
                          analyticsData.batch_wise.map((batch, index) => {
                            const total = Number(batch?.total_student || 0);

                            const present = Number(batch?.present || 0);

                            const percentage =
                              total > 0
                                ? Math.floor((present / total) * 100)
                                : 0;

                            return (
                              <tr
                                key={batch?.batch_id || index}
                                style={{
                                  cursor: "pointer",
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#MainModal"
                                onClick={() =>
                                  openStudentReport({
                                    batch_id: batch?.batch_id,
                                  })
                                }
                              >
                                <td className="ps-4 fw-semibold">
                                  {index + 1}
                                </td>

                                <td>
                                  <div className="fw-semibold">
                                    {batch?.batch_name || "-"}
                                  </div>

                                  {batch?.class_teacher_name && (
                                    <small className="text-muted">
                                      Teacher: {batch.class_teacher_name}
                                    </small>
                                  )}
                                </td>

                                <td className="text-center fw-semibold">
                                  {total}
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-success-subtle text-success px-3 py-2">
                                    {batch?.present || 0}
                                  </span>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-danger-subtle text-danger px-3 py-2">
                                    {batch?.absent || 0}
                                  </span>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-warning-subtle text-warning px-3 py-2">
                                    {batch?.no_marked || 0}
                                  </span>
                                </td>

                                <td
                                  style={{
                                    minWidth: "140px",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="progress flex-grow-1"
                                      style={{
                                        height: "7px",
                                      }}
                                    >
                                      <div
                                        className={`progress-bar ${
                                          percentage >= 75
                                            ? "bg-success"
                                            : percentage >= 50
                                              ? "bg-warning"
                                              : "bg-danger"
                                        }`}
                                        style={{
                                          width: `${percentage}%`,
                                        }}
                                      ></div>
                                    </div>

                                    <small className="fw-bold">
                                      {percentage}%
                                    </small>
                                  </div>
                                </td>

                                <td className="text-center no-print">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      openStudentReport({
                                        batch_id: batch?.batch_id,
                                      });
                                    }}
                                    data-bs-toggle="modal"
                                    data-bs-target="#MainModal"
                                  >
                                    <i className="ri-eye-line me-1"></i>
                                    View
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center py-5 text-muted"
                            >
                              <i className="ri-inbox-line fs-1 d-block mb-2"></i>
                              No batch attendance data found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* =================================================
                                    PRINT FOOTER
                                ================================================= */}

                <div className="d-none d-print-block mt-4">
                  <div className="border-top pt-3">
                    <div className="row">
                      <div className="col-6">
                        <small>
                          Session:{" "}
                          {sessions?.find(
                            (s) => String(s.id) === String(filters?.session_id),
                          )?.session || "-"}
                        </small>
                      </div>

                      <div className="col-6 text-end">
                        <small>Date: {filters?.date}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
                QUICK REPORT MODAL
            ========================================================= */}

      <div
        className="modal fade"
        id="QuickReportModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold">
                  {quickReport === "message" && "Message Report"}

                  {quickReport === "lowAttendance" && "Low Attendance Report"}

                  {quickReport === "attendanceSummary" && "Attendance Summary"}

                  {quickReport === "longAbsent" && "Long Absent Students"}

                  {quickReport === "staffWise" && "Staff Wise Attendance"}
                </h5>

                <small className="text-muted">
                  {quickReport === "message" &&
                    "View Push Notification and Meta WhatsApp delivery status"}

                  {quickReport === "lowAttendance" &&
                    "Students whose attendance is below 75%"}

                  {quickReport === "attendanceSummary" &&
                    "Attendance summary for selected date range"}

                  {quickReport === "longAbsent" && "Students with long absence"}

                  {quickReport === "staffWise" &&
                    "Attendance report staff wise"}
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              {/* =================================================
                                MESSAGE REPORT
                            ================================================= */}

              {quickReport === "message" && (
                <div className="row g-3">
                  {/* PUSH */}

                  <div className="col-md-6">
                    <div className="card border h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-3 bg-primary-subtle text-primary p-3 me-3">
                            <i className="ri-notification-3-line fs-4"></i>
                          </div>

                          <div>
                            <h5 className="fw-bold mb-0">Push Notification</h5>

                            <small className="text-muted">
                              App notification delivery
                            </small>
                          </div>
                        </div>

                        <div className="row g-2">
                          <div className="col-6">
                            <div className="p-3 bg-warning-subtle rounded">
                              <div className="small text-muted">Not Sent</div>

                              <h5 className="fw-bold text-warning mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.push_not_sent || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-success-subtle rounded">
                              <div className="small text-muted">Sent</div>

                              <h5 className="fw-bold text-success mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.push_sent || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-info-subtle rounded">
                              <div className="small text-muted">Processing</div>

                              <h5 className="fw-bold text-info mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.push_processing || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-danger-subtle rounded">
                              <div className="small text-muted">Failed</div>

                              <h5 className="fw-bold text-danger mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.push_failed || 0
                                )}
                              </h5>
                            </div>
                          </div>
                        </div>

                        <hr />

                        <div className="d-grid gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-warning"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "push_not_send",
                              })
                            }
                          >
                            View Not Sent Students
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-success"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "push_send",
                              })
                            }
                          >
                            View Sent Students
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "push_failed",
                              })
                            }
                          >
                            View Failed Students
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* META */}

                  <div className="col-md-6">
                    <div className="card border h-100">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-3 bg-success-subtle text-success p-3 me-3">
                            <i className="ri-whatsapp-line fs-4"></i>
                          </div>

                          <div>
                            <h5 className="fw-bold mb-0">Meta WhatsApp</h5>

                            <small className="text-muted">
                              WhatsApp message delivery
                            </small>
                          </div>
                        </div>

                        <div className="row g-2">
                          <div className="col-6">
                            <div className="p-3 bg-warning-subtle rounded">
                              <div className="small text-muted">Not Sent</div>

                              <h5 className="fw-bold text-warning mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.message_not_sent || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-success-subtle rounded">
                              <div className="small text-muted">Sent</div>

                              <h5 className="fw-bold text-success mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.message_sent || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-info-subtle rounded">
                              <div className="small text-muted">Processing</div>

                              <h5 className="fw-bold text-info mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.message_processing || 0
                                )}
                              </h5>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="p-3 bg-danger-subtle rounded">
                              <div className="small text-muted">Failed</div>

                              <h5 className="fw-bold text-danger mb-0">
                                {loading ? (
                                  <LoadingDivShort />
                                ) : (
                                  data?.message_failed || 0
                                )}
                              </h5>
                            </div>
                          </div>
                        </div>

                        <hr />

                        <div className="d-grid gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-warning"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "meta_not_send",
                              })
                            }
                          >
                            View Not Sent Students
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-success"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "meta_send",
                              })
                            }
                          >
                            View Sent Students
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#MainModal"
                            onClick={() =>
                              openStudentReport({
                                message_filter: "meta_failed",
                              })
                            }
                          >
                            View Failed Students
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* =================================================
                                LOW ATTENDANCE
                            ================================================= */}

              {quickReport === "lowAttendance" && (
                <div>
                  <div className="alert alert-danger d-flex align-items-center">
                    <i className="ri-alert-line fs-4 me-2"></i>

                    <div>
                      <strong>
                        {loadingAnalytics ? (
                          <LoadingDivShort />
                        ) : (
                          analyticsData?.low_attendance_below_75 || 0
                        )}
                      </strong>{" "}
                      students have attendance below 75%.
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          table: "student",
                          low_attendance: "1",
                        })
                      }
                    >
                      <i className="ri-eye-line me-1"></i>
                      View Low Attendance Students
                    </button>
                  </div>
                </div>
              )}

              {/* =================================================
                                ATTENDANCE SUMMARY
                            ================================================= */}

              {quickReport === "attendanceSummary" && (
                <div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="p-4 bg-success-subtle rounded-3 text-center">
                        <i className="ri-checkbox-circle-line text-success fs-2"></i>

                        <h3 className="fw-bold text-success mt-2 mb-1">
                          {loadingSummary ? (
                            <LoadingDivShort />
                          ) : (
                            dataSummary?.regular_present || 0
                          )}
                        </h3>

                        <div>Regular Present</div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="p-4 bg-danger-subtle rounded-3 text-center">
                        <i className="ri-close-circle-line text-danger fs-2"></i>

                        <h3 className="fw-bold text-danger mt-2 mb-1">
                          {loadingSummary ? (
                            <LoadingDivShort />
                          ) : (
                            dataSummary?.absent_present || 0
                          )}
                        </h3>

                        <div>Regular Absent</div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="p-4 bg-warning-subtle rounded-3 text-center">
                        <i className="ri-bar-chart-line text-warning fs-2"></i>

                        <h3 className="fw-bold text-warning mt-2 mb-1">
                          {loadingAnalytics ? (
                            <LoadingDivShort />
                          ) : (
                            analyticsData?.attendance_summary?.absent_present ||
                            0
                          )}
                        </h3>

                        <div>Absent / Present</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="row align-items-center g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          From Date
                        </label>

                        <input
                          type="date"
                          className="form-control"
                          value={filters?.from_date || ""}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              from_date: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          To Date
                        </label>

                        <input
                          type="date"
                          className="form-control"
                          value={filters?.to_date || ""}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              to_date: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <button
                      type="button"
                      className="btn btn-primary"
                      data-bs-dismiss="modal"
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          table: "attendance_summary",
                          attendance_type: "both",
                        })
                      }
                    >
                      <i className="ri-eye-line me-1"></i>
                      View Detailed Summary
                    </button>
                  </div>
                </div>
              )}

              {/* =================================================
                                LONG ABSENT
                            ================================================= */}

              {quickReport === "longAbsent" && (
                <div>
                  <div className="text-center py-3">
                    <div className="rounded-circle bg-warning-subtle text-warning d-inline-flex p-4 mb-3">
                      <i className="ri-user-unfollow-line fs-1"></i>
                    </div>

                    <h3 className="fw-bold">
                      {loadingAnalytics ? (
                        <LoadingDivShort />
                      ) : (
                        analyticsData?.long_absent || 0
                      )}
                    </h3>

                    <p className="text-muted">
                      Students are currently marked as long absent.
                    </p>

                    <button
                      type="button"
                      className="btn btn-warning"
                      data-bs-dismiss="modal"
                      data-bs-toggle="modal"
                      data-bs-target="#MainModal"
                      onClick={() =>
                        openStudentReport({
                          table: "student",
                          date: filters?.date,
                          long_absent: "1",
                        })
                      }
                    >
                      <i className="ri-eye-line me-1"></i>
                      View Long Absent Students
                    </button>
                  </div>
                </div>
              )}

              {/* =================================================
                                STAFF WISE ATTENDANCE
                            ================================================= */}

              {quickReport === "staffWise" && (
                <div>
                  {loadingAnalytics ? (
                    <div className="text-center py-5">
                      <LoadingDivShort />
                    </div>
                  ) : staffWiseData?.length ? (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>

                            <th>Staff</th>

                            <th className="text-center">Total Students</th>

                            <th className="text-center text-success">
                              Present
                            </th>

                            <th className="text-center text-danger">Absent</th>

                            <th className="text-center text-warning">
                              Not Marked
                            </th>

                            <th className="text-center">Attendance</th>
                          </tr>
                        </thead>

                        <tbody>
                          {staffWiseData.map((staff, index) => {
                            const total = Number(staff?.total_student || 0);

                            const present = Number(staff?.present || 0);

                            const absent = Number(staff?.absent || 0);

                            const notMarked = Number(staff?.not_marked || 0);

                            const percentage =
                              total > 0
                                ? Math.round((present / total) * 100)
                                : 0;

                            return (
                              <tr key={staff?.staff_id || index}>
                                <td className="fw-semibold">{index + 1}</td>

                                <td>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center me-2"
                                      style={{
                                        width: "38px",
                                        height: "38px",
                                      }}
                                    >
                                      <i className="ri-user-line"></i>
                                    </div>

                                    <div>
                                      <div className="fw-semibold">
                                        {staff?.staff_name || "-"}
                                      </div>

                                      <small className="text-muted">
                                        Staff ID: {staff?.staff_id || "-"}
                                      </small>
                                    </div>
                                  </div>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-primary-subtle text-primary px-3 py-2">
                                    {total}
                                  </span>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-success-subtle text-success px-3 py-2">
                                    {present}
                                  </span>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-danger-subtle text-danger px-3 py-2">
                                    {absent}
                                  </span>
                                </td>

                                <td className="text-center">
                                  <span className="badge bg-warning-subtle text-warning px-3 py-2">
                                    {notMarked}
                                  </span>
                                </td>

                                <td
                                  style={{
                                    minWidth: "160px",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="progress flex-grow-1"
                                      style={{
                                        height: "7px",
                                      }}
                                    >
                                      <div
                                        className={`progress-bar ${
                                          percentage >= 75
                                            ? "bg-success"
                                            : percentage >= 50
                                              ? "bg-warning"
                                              : "bg-danger"
                                        }`}
                                        style={{
                                          width: `${percentage}%`,
                                        }}
                                      ></div>
                                    </div>

                                    <small className="fw-bold">
                                      {percentage}%
                                    </small>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <i className="ri-inbox-line fs-1 d-block mb-2"></i>
                      No staff wise attendance data found.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
                EXISTING STUDENT DETAIL MODAL
            ========================================================= */}

      <DashboardModal
        setLoading={setLoading}
        allFilter={allFilters}
        setAllFilter={setAllFilters}
        dataSummary={dataSummary}
        isModal={true}
        refreshDashboard={refreshDashboard}
      />
    </>
  );
}
